import { Link } from 'react-router-dom';
import { useParallax } from '../../hooks/useParallax';

export default function CTABanner() {
  const bgOffset = useParallax(0.25);
  const patternOffset = useParallax(0.15);

  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: '#F5C518' }}>
      {/* Checkered flag pattern - top left */}
      <div
        className="absolute top-0 left-0 w-[400px] h-full opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(45deg, #000 25%, transparent 25%),
            linear-gradient(-45deg, #000 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #000 75%),
            linear-gradient(-45deg, transparent 75%, #000 75%)
          `,
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
          maskImage: 'linear-gradient(to right, rgba(0,0,0,0.8), transparent)',
          WebkitMaskImage: 'linear-gradient(to right, rgba(0,0,0,0.8), transparent)',
          transform: `translate3d(0, ${patternOffset}px, 0)`,
          willChange: 'transform'
        }}
      />

      {/* Checkered flag pattern - bottom right */}
      <div
        className="absolute top-0 right-0 w-[400px] h-full opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(45deg, #000 25%, transparent 25%),
            linear-gradient(-45deg, #000 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #000 75%),
            linear-gradient(-45deg, transparent 75%, #000 75%)
          `,
          backgroundSize: '40px 40px',
          backgroundPosition: '0 0, 0 20px, 20px -20px, -20px 0px',
          maskImage: 'linear-gradient(to left, rgba(0,0,0,0.8), transparent)',
          WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.8), transparent)',
          transform: `translate3d(0, ${patternOffset}px, 0)`,
          willChange: 'transform'
        }}
      />

      {/* Diagonal checker stripe accent - top */}
      <div
        className="absolute top-0 left-0 right-0 h-[6px] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(45deg, #000 25%, transparent 25%),
            linear-gradient(-45deg, #000 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #000 75%),
            linear-gradient(-45deg, transparent 75%, #000 75%)
          `,
          backgroundSize: '12px 12px',
          backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
          backgroundColor: '#fff',
          opacity: 0.5,
        }}
      />

      {/* Subtle gradient overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-black/5 pointer-events-none"
        style={{ transform: `translate3d(0, ${bgOffset}px, 0)`, willChange: 'transform' }}
      />

      {/* Bottom gradient fade into race track */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[120px] pointer-events-none z-[5]"
        style={{
          background: 'linear-gradient(to bottom, transparent 0%, rgba(15,15,15,0.15) 30%, rgba(15,15,15,0.5) 60%, rgba(10,10,10,0.85) 80%, #0a0a0a 100%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-20 pb-28 flex flex-col items-center text-center">
        {/* Logo / Brand */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-14 h-10 rounded-lg bg-black/90 flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
              iBen
            </span>
          </div>
          <div className="w-[1px] h-8 bg-black/20" />
          <span className="text-black/70 text-sm font-semibold tracking-widest uppercase">
            Consulting
          </span>
        </div>

        {/* Eyebrow */}
        <p className="text-black/50 text-xs font-medium tracking-widest uppercase mb-4">
          Silicon Valley&apos;s Trusted IT Partner Since 2014
        </p>

        {/* Headline */}
        <h2
          className="text-4xl md:text-5xl font-bold text-black leading-[1.35] mb-6 max-w-3xl"
        >
          If we can keep the F1 car below on track,
          <br className="hidden sm:block" />
          {' '}imagine what we can do for your IT.
        </h2>

        {/* Subtext */}
        <p className="text-black/55 text-sm md:text-base max-w-xl mb-9 leading-relaxed">
          Enterprise-grade cybersecurity, AI-powered solutions, and 24/7 managed IT — engineered for peak performance.
        </p>

        {/* CTA Button */}
        <Link
          to="/contact"
          className="inline-flex items-center justify-center gap-2.5 bg-black hover:bg-gray-900 text-white px-8 py-4 rounded-lg text-[15px] font-semibold tracking-wide transition-all duration-300 whitespace-nowrap cursor-pointer shadow-xl hover:shadow-2xl hover:-translate-y-[2px]"
        >
          <i className="ri-chat-1-line w-5 h-5 flex items-center justify-center" />
          Talk to Us
        </Link>

        {/* Trust badges - single row */}
        <div className="mt-10 flex items-center justify-center gap-3 text-sm text-black/40">
          <div className="flex items-center justify-center gap-2">
            <i className="ri-time-fill text-black/30 w-4 h-4 flex items-center justify-center" />
            <span>24/7 Support</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-black/20" />
          <div className="flex items-center justify-center gap-2">
            <i className="ri-team-fill text-black/30 w-4 h-4 flex items-center justify-center" />
            <span>500+ Clients</span>
          </div>
        </div>
      </div>
    </section>
  );
}
